import { cookieManager } from "@/service/cookieManager";
import store from "@/store/index";
import { detectOS } from "@/utils/commonUtils";
import WeWingmanWorker from "@/service/worker/weWingmanWorker.worker.js";

let worker = null;

const state = {
	connectionStatus: "disconnected", // 'disconnected' | 'connecting' | 'connected'
	messages: [],
};

const getters = {
	isConnected: (state) => state.connectionStatus === "connected",
	connectionStatus: (state) => state.connectionStatus,
	messages: (state) => state.messages,
	trafficLightStatus: (state) => {
		const map = { connected: "green", connecting: "yellow", disconnected: "red" };
		return map[state.connectionStatus] || "red";
	},
};

const mutations = {
	SET_STATUS(state, status) {
		state.connectionStatus = status;
	},
	ADD_MESSAGE(state, message) {
		state.messages.push(message);
	},
	CLEAR_MESSAGES(state) {
		state.messages = [];
	},
};

const actions = {
	connectToWebSocket({ commit, dispatch }) {
		return new Promise((resolve, reject) => {
			if (worker) {
				worker.terminate();
			}

			worker = new WeWingmanWorker();
			commit("SET_STATUS", "connecting");

			let resolved = false;

			worker.onmessage = (event) => {
				const { type, data, error } = event.data;

				switch (type) {
					case "connected":
						commit("SET_STATUS", "connected");
						dispatch("sendMessage", { action: "GET_VERSION", data: null });
						if (!resolved) {
							resolved = true;
							resolve();
						}
						break;

					case "connecting":
						commit("SET_STATUS", "connecting");
						break;

					case "disconnected":
						commit("SET_STATUS", "disconnected");
						if (!resolved) {
							resolved = true;
							reject(new Error("WebSocket connection closed"));
						}
						break;

					case "error":
						console.error("[WeWingman] Worker error:", error);
						commit("SET_STATUS", "disconnected");
						if (!resolved) {
							resolved = true;
							reject(new Error(error));
						}
						break;

					case "message":
						commit("ADD_MESSAGE", data);
						dispatch("handleIncomingMessage", data);
						break;
				}
			};

			worker.postMessage({
				type: "connect",
				payload: { url: "ws://localhost:7007" },
			});
		});
	},

	checkAndConnectToWeWingman({ state, dispatch }) {
		if (state.connectionStatus === "connected") {
			console.log("WeWingman is already connected.");
			testAppDeepLink(store);
			return;
		}

		console.log("Attempting to connect to WebSocket...");
		dispatch("connectToWebSocket")
			.then(() => {
				console.log("WebSocket connection established successfully.");
			})
			.catch(() => {
				console.warn("Error conectando a websocket. Intentando abrir servicio de impresión WeWelcom...");
				testAppDeepLink(store);
			});
	},
	async openCashDrawer({ dispatch }) {
		if (state.connectionStatus === "connected") {
			await dispatch("sendMessage", { action: "OPEN_CASH_DRAWER", data: {} });
		}
	},
	async printBill({ dispatch }, payload) {
		const bill = payload.bill;
		const openCashDrawer = payload.openCashDrawer ?? false;

		if (!bill || !bill.id) {
			console.warn("No bill provided to printBill");
			return;
		}

		const data = { id: bill.id };
		let billPrintInfoResult = await dispatch("copilot/getBillPrintInfo", data, { root: true });

		const currentOS = detectOS();
		console.log("vamos a imprimir en bill" + currentOS);

		let options = {
			message: billPrintInfoResult.escPosText,
			cut: true,
			printLogo: false,
			postFeedMm: 0,
			openCashDrawer: openCashDrawer,
		};

		let printData = {
			message: billPrintInfoResult.escPosText,
			options: options,
			cut: true,
			printLogo: false,
			postFeedMm: 0,
		};

		dispatch("sendMessage", { action: "PRINT", data: printData });
	},

	async printBatch({ dispatch }, payload) {
		const { jobs } = payload;
		dispatch("sendMessage", { action: "PRINT_BATCH", data: { jobs } });
	},

	async buildBatchPayload(vouchers, common = {}) {
		const jobs = vouchers.map((text) => ({
			message: text,
			options: { cut: true, ...common },
		}));

		return { action: "PRINT_BATCH", data: { jobs } };
	},

	async printText({ dispatch }, escPosText) {
		const currentOS = detectOS();
		console.log("vamos a imprimir en text" + currentOS);

		let printData = {
			message: escPosText,
			cut: true,
			printLogo: false,
			postFeedMm: 0,
		};

		dispatch("sendMessage", { action: "PRINT", data: printData });
	},
	disconnectWebSocket({ commit }) {
		if (worker) {
			worker.postMessage({ type: "disconnect" });
			worker.terminate();
			worker = null;
		}
		commit("SET_STATUS", "disconnected");
		console.log("WebSocket connection closed by client");
	},
	sendMessage(_, { action, data }) {
		if (worker && state.connectionStatus === "connected") {
			worker.postMessage({
				type: "send",
				payload: { action, data },
			});
		} else {
			console.error("WebSocket is not connected");
		}
	},
	handleIncomingMessage(_, message) {
		try {
			const parsed = typeof message === "string" ? JSON.parse(message) : message;
			const { status, data, message: serverMessage } = parsed;

			console.log("Server Response:", { status, data, serverMessage });

			if (status === "success") {
				switch (data.action) {
					case "GET_VERSION":
						console.log("Server version:", data.version);
						break;
					case "LIST_PRINTERS":
						console.log("Available printers:", data.printers);
						break;
					default:
						console.warn("Unhandled successful action:", data);
				}
			} else if (status === "error") {
				console.error("Error from server:", serverMessage);
			} else {
				console.warn("Unknown status received:", status);
			}
		} catch (error) {
			console.error("Error parsing server message:", error);
		}
	},
	downloadApk({ rootGetters }) {
		const venueWewelcomUrl = rootGetters["venue/urlVenue"];
		const apkUrl = `${venueWewelcomUrl}/world/api/wewingman/download`;
		console.log("WeWingman download URL:", apkUrl);

		const token = cookieManager.getToken();

		fetch(apkUrl, {
			method: "GET",
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error("Failed to download APK");
				}

				const disposition = response.headers.get("Content-Disposition");
				let filename = "download.apk";

				if (disposition && disposition.includes("filename=")) {
					const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
					if (filenameMatch.length > 1) {
						filename = filenameMatch[1];
					}
				}

				return response.blob().then((blob) => ({ blob, filename }));
			})
			.then(({ blob, filename }) => {
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);
				console.log(`APK downloaded successfully as ${filename}`);
			})
			.catch((error) => {
				console.error("Error downloading APK:", error);
				alert("There was an issue downloading the APK. Please try again later.");
			});
	},
};
const testAppDeepLink = (store) => {
	let deepLinkOpened = false;

	const iframe = document.createElement("iframe");
	iframe.style.display = "none";
	iframe.src = "wewelcom://wewingman";

	document.body.appendChild(iframe);

	const timeout = setTimeout(() => {
		if (!deepLinkOpened) {
			alert("El Servicio de impresión WeWelcom no está instalado. Intentando descargar la aplicación...");
			store
				.dispatch("weWingman/downloadApk")
				.then(() => {
					console.log("APK downloaded successfully");
				})
				.catch((error) => {
					console.error("Failed to download APK:", error);
					alert("Hubo un problema descargando la aplicación. Por favor, inténtelo más tarde.");
				});
		}
		document.body.removeChild(iframe);
	}, 1000);

	window.addEventListener("blur", () => {
		deepLinkOpened = true;
		clearTimeout(timeout);
	});
};

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions,
};
